import {
  Trace,
  TracePoint,
  TwoParamCommand,
  SingleParamCommand,
} from '@engoo/realtime-protocol/draw/trace';
import { DrawAction, IPencilAction, IEraserAction, IAddAction, DrawActionType } from '@engoo/realtime-protocol/draw';
import { makeInt } from 'app/utils/ioTs';
import * as iots from 'io-ts';
import { assertUnreachable, PickByType } from 'app/utils/types';

/**
 * All actions that beginnings of traces
 */
export type StartTraceAction = IPencilAction | IEraserAction;

/**
 * All action types that have trace field
 */
export type TraceAction = StartTraceAction | IAddAction;

/**
 * Type-guard against passing through undesirable types in default
 * cases for switch statements
 */
const denyAction = <T>(action: Exclude<DrawAction, T>) => {};

export const isTraceAction = (action: DrawAction): action is TraceAction => {
  switch (action.type) {
    case DrawActionType.Pencil:
    case DrawActionType.Eraser:
    case DrawActionType.Add:
      return true;
    default:
      denyAction<TraceAction>(action);
      return false;
  }
};

export const isStartTraceAction = (action: TraceAction): action is StartTraceAction => {
  switch (action.type) {
    case DrawActionType.Pencil:
    case DrawActionType.Eraser:
      return true;
    default:
      denyAction<StartTraceAction>(action);
      return false;
  }
};

export type TraceType = StartTraceAction['type'];

/**
 * Subset of DrawActionType enum with trace types only
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TraceType: PickByType<typeof DrawActionType, TraceType> = {
  Pencil: DrawActionType.Pencil,
  Eraser: DrawActionType.Eraser,
};

export interface IIntPoint {
  readonly x: iots.Int,
  readonly y: iots.Int,
}

/**
 * Converts series of `TracePoint`s to a series of `IIntPoint`s
 */
export const traceToIntPoints = (trace: Iterable<TracePoint>): IIntPoint[] => {
  let x = makeInt(0);
  let y = makeInt(0);
  const result: IIntPoint[] = [];

  for (const point of trace) {
    switch (point[0]) {
      case TwoParamCommand.MoveAbsolute:
      case TwoParamCommand.LineAbsolute: {
        const [, px, py] = point;
        x = px;
        y = py;
        break;
      }
      case TwoParamCommand.MoveRelative:
      case TwoParamCommand.LineRelative: {
        const [, px, py] = point;
        x = makeInt(x + px);
        y = makeInt(y + py);
        break;
      }
      case SingleParamCommand.HorizontalAbsolute:
        x = point[1];
        break;
      case SingleParamCommand.VerticalAbsolute:
        y = point[1];
        break;
      case SingleParamCommand.HorizontalRelative:
        x = makeInt(x + point[1]);
        break;
      case SingleParamCommand.VerticalRelative:
        y = makeInt(y + point[1]);
        break;
      default:
        assertUnreachable(point[0]);
    }

    result.push({ x, y });
  }

  return result;
};

/**
 * Converts `IIntPoint`s to a trace
 *
 * Requires to pass in what the first command in the trace
 * should be. For example for new traces this should be
 * `TwoParamCommand.MoveAbsolute` and for continuations it should be
 * `TwoParamCommand.LineAbsolute`.
 *
 * Please note that converting trace -> points -> trace can produce
 * a different output to the original input. The difference is only
 * in use of relative or absolute trace points. The resulting trace
 * will still represent the same set of points as the original.
 */
export const intPointsToTrace = (firstCmd: TwoParamCommand, points: IIntPoint[]): Trace => {
  let x = makeInt(0);
  let y = makeInt(0);

  return points.map((p, index) => {
    const tracePoint: TracePoint = (
      index === 0 ? [firstCmd, p.x, p.y] :
      p.x === x ? [SingleParamCommand.VerticalRelative, makeInt(p.y - y)] :
      p.y === y ? [SingleParamCommand.HorizontalRelative, makeInt(p.x - x)] :
      [TwoParamCommand.LineRelative, makeInt(p.x - x), makeInt(p.y - y)]
    );

    x = p.x;
    y = p.y;

    return tracePoint;
  });
};
